import { useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import axios from "axios";
import moment from "moment";
import fileDownload from 'js-file-download';
import { Add, ArrowBack, Description, GetApp, Upload } from "@mui/icons-material";
import { Box, CircularProgress, Tab, Tabs, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import Layout from "../../../Components/Layout/Layout";
import PaperTable from "../../../Components/PaperTable";
import Sidebar from "Components/Sidebar/Sidebar";
import SidebarGroup from "Components/Sidebar/SidebarGroup";
import SidebarButton from "Components/Sidebar/SidebarButton";
import showMessage from "Dialogs/showMessage";
import useAsyncEffect from "../../../system/useAsyncEffect";
import useCompanies from "../../../system/useCompanies";
import parseContentDispositionFilename from 'system/parseContentDispositionFilename';
import dateFormats from "system/dateFormats";
import { TransportTariff } from "../../../system/Domain";
import { axleLabels, billingLabels, getRouteLabel } from "./tariffLabels";

const downloadFile = async (url: string, params: object) => {
	const response = await axios.get(url, { responseType: "blob", params });
	fileDownload(response.data, parseContentDispositionFilename(response.headers["content-disposition"]));
};

const TransportCarrierPage = () => {
	const history = useHistory();
	const { companyId } = useParams<{ companyId: string }>();
	const [companies] = useCompanies();
	const [tariffs, setTariffs] = useState<TransportTariff[]>();
	const [reloadCounter, setReloadCounter] = useState(0);
	const fileInput = useRef<HTMLInputElement>(null);

	const company = companies?.find(c => c.id === Number(companyId));

	useAsyncEffect(async (cancelToken) => {
		const { data } = await axios.get<TransportTariff[]>(`/api/transport-tariffs`, {
			params: { carrierId: companyId },
			cancelToken: cancelToken.getAxiosToken()
		});

		setTariffs(data);
	}, [companyId, reloadCounter]);

	const upload = async (file: File) => {
		const body = new FormData();
		body.append("file", file);

		try {
			// 400 carries the row errors and is handled here instead of by the global error toast
			const { data, status } = await axios.post<{ tariffs: number; prices: number; errors?: string[] }>(
				`/api/transport-tariffs/import`, body, { params: { carrierId: companyId }, validateStatus: s => (s >= 200 && s < 300) || s === 400 }
			);

			if (status === 400) {
				throw { response: { data } };
			}

			await showMessage({
				title: "Upload erfolgreich",
				message: `${data.tariffs} Tarife mit ${data.prices} Preiszeilen wurden angelegt.`
			});
			setReloadCounter(c => c + 1);
		} catch (error: any) {
			const errors: string[] | undefined = error?.response?.data?.errors;
			if (!errors) {
				if (error?.response?.status) {
					throw error;
				}

				await showMessage({ title: "Upload abgelehnt", color: "error", message: "Die Datei wurde abgelehnt, es wurde nichts gespeichert." });
				return;
			}

			await showMessage({
				title: "Upload abgelehnt, es wurde nichts gespeichert",
				color: "error",
				maxWidth: "md",
				message: (
					<>
						{errors.slice(0, 30).map((e, i) => <Typography key={i}>{e}</Typography>)}
						{errors.length > 30 && <Typography>… und {errors.length - 30} weitere Fehler</Typography>}
					</>
				)
			});
		}
	};

	return (
		<Layout
			title={company ? `Dienstleister – ${company.name}` : "Dienstleister"}
			search="disabled"
			sidebar={(
				<Sidebar>
					<SidebarGroup>
						<SidebarButton
							color="primary"
							startIcon={<Add />}
							label="Neuer Tarif"
							onClick={() => history.push(`/companies/${companyId}/tariffs/new`)}
						/>
					</SidebarGroup>
					<SidebarGroup>
						<SidebarButton
							startIcon={<Description />}
							label="Excel-Vorlage"
							onClick={() => downloadFile(`/api/transport-tariffs/template`, { carrierId: companyId })}
						/>
						<SidebarButton
							startIcon={<Upload />}
							label="Excel hochladen"
							onClick={() => fileInput.current?.click()}
						/>
						<SidebarButton
							startIcon={<GetApp />}
							label="Excel-Export"
							onClick={() => downloadFile(`/api/transport-tariffs`, { carrierId: companyId, output: "xlsx" })}
						/>
					</SidebarGroup>
					<SidebarGroup>
						<SidebarButton
							startIcon={<ArrowBack />}
							label="Alle Dienstleister"
							onClick={() => history.push("/companies")}
						/>
					</SidebarGroup>
				</Sidebar>
			)}
		>
			<input
				ref={fileInput}
				type="file"
				accept=".xlsx"
				hidden
				onChange={async e => {
					const file = e.target.files?.[0];
					e.target.value = "";
					if (file) {
						await upload(file);
					}
				}}
			/>
			<Tabs value="tariffs" onChange={(_, value) => value === "statistics" && history.push("/companies")}>
				<Tab value="statistics" label="Statistik" />
				<Tab value="tariffs" label="Tarife" />
			</Tabs>
			<Box mt={2}>
				{!tariffs && <CircularProgress size={48} />}
				{tariffs && (
					<PaperTable>
						<TableHead>
							<TableRow>
								<TableCell>Achse</TableCell>
								<TableCell>Abrechnung</TableCell>
								<TableCell>Strecke</TableCell>
								<TableCell>Gültig</TableCell>
								<TableCell>Preiszeilen</TableCell>
								<TableCell>Dieselzuschlag</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{tariffs.map(t => (
								<TableRow
									key={t.id}
									hover
									onClick={() => history.push(`/companies/${companyId}/tariffs/${t.id}`)}
								>
									<TableCell>{axleLabels[t.axle]}</TableCell>
									<TableCell>{billingLabels[t.billing]}</TableCell>
									<TableCell>{getRouteLabel(t)}</TableCell>
									<TableCell>
										{moment(t.validFrom).format(dateFormats.date)}
										{" – "}
										{t.validTo ? moment(t.validTo).format(dateFormats.date) : "offen"}
									</TableCell>
									<TableCell>{t.prices?.length ?? 0}</TableCell>
									<TableCell>{t.dieselSurchargeApplies ? "Ja" : "Nein"}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</PaperTable>
				)}
				{tariffs?.length === 0 && (
					<Typography mt={2}>Für diesen Dienstleister sind noch keine Tarife angelegt.</Typography>
				)}
			</Box>
		</Layout>
	);
};

export default TransportCarrierPage;
